import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, features } from '../data/mock';
import { ArrowRight, ArrowUpRight, Shield, Truck, Sparkles, Lock, HeartHandshake, Eye, Feather, Sun } from 'lucide-react';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const { addToCart } = useCart();
  const featuredProducts = products.slice(0, 4);

  useEffect(() => {
    document.title = 'Clarity Lens Co | Eyewear & Vision Accessories';
  }, []);

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1,
    });
  };

  const benefitIcons = [Eye, Feather, Sparkles, Sun];
  const whyPoints = [
    { title: 'Quality Materials', desc: 'Premium acetate, titanium and hand-finished frames built to last.' },
    { title: 'USA Shipping', desc: 'Fast, tracked shipping from within the United States on every order.' },
    { title: 'Secure Checkout', desc: 'Encrypted payments with fraud protection and transparent billing.' },
    { title: 'Human Support', desc: 'Real people ready to help with fit, lenses and anything in between.' },
  ];

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <CartDrawer />

      <main className="pt-[116px] lg:pt-[136px]">
        {/* ====================================================== */}
        {/* 1. HERO - Editorial asymmetric split                    */}
        {/* ====================================================== */}
        <section className="relative">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 pb-20 lg:pb-28">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
              {/* Left copy block - 7 cols */}
              <div className="lg:col-span-7 lg:pr-12 relative z-10">
                <div className="flex items-center gap-3 mb-8">
                  <span className="w-10 h-px bg-copper-500" />
                  <span className="text-eyebrow text-copper-600 font-mono">The Clarity Collection — 2025</span>
                </div>
                <h1 className="font-display text-ink-900 text-hero-xl">
                  See <span className="italic font-light">clearly.</span>
                  <br />
                  Live <span className="italic font-light text-copper-600">beautifully.</span>
                </h1>
                <p className="mt-8 text-ink-600 text-lg max-w-xl leading-relaxed">
                  Precision-crafted eyewear and vision accessories for the modern everyday — engineered for clarity,
                  finished for comfort, designed for a lifetime.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link
                    to="/shop"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-ink-900 text-cream-50 hover:bg-copper-600 transition-colors duration-300"
                  >
                    <span className="text-sm tracking-[0.15em] uppercase font-mono">Shop Now</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/why-choose-us"
                    className="inline-flex items-center gap-2 px-8 py-4 border border-ink-300 text-ink-900 hover:border-ink-900 transition-colors"
                  >
                    <span className="text-sm tracking-[0.15em] uppercase font-mono">Why Clarity</span>
                  </Link>
                </div>

                {/* Mini featured strip */}
                <div className="mt-16 pt-8 border-t border-ink-200 grid grid-cols-3 gap-6 max-w-md">
                  <div>
                    <p className="font-display text-2xl text-ink-900">8<span className="text-copper-600">+</span></p>
                    <p className="text-xs text-ink-500 tracking-wider mt-1">Styles</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl text-ink-900">30<span className="text-copper-600">d</span></p>
                    <p className="text-xs text-ink-500 tracking-wider mt-1">Easy Returns</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl text-ink-900">USA</p>
                    <p className="text-xs text-ink-500 tracking-wider mt-1">Shipping</p>
                  </div>
                </div>
              </div>

              {/* Right image stack - 5 cols */}
              <div className="lg:col-span-5 relative">
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={siteImages.heroMain}
                    alt="Clarity Lens Co lifestyle"
                    className="w-full h-full object-cover grayscale-[0.1]"
                  />
                  {/* Overlay tag */}
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                    <div className="bg-cream-50/95 backdrop-blur-sm px-4 py-3 max-w-[220px]">
                      <p className="text-[10px] tracking-[0.25em] uppercase text-copper-600 font-mono">Editor’s Pick</p>
                      <p className="font-display text-lg text-ink-900 leading-tight mt-1">Polarized UV Sunglasses</p>
                    </div>
                    <Link
                      to="/product/polarized-uv-sunglasses"
                      className="w-12 h-12 bg-ink-900 text-cream-50 flex items-center justify-center hover:bg-copper-600 transition-colors"
                    >
                      <ArrowUpRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
                {/* Floating caption */}
                <div className="hidden lg:block absolute -left-6 top-8 rotate-[-90deg] origin-top-left">
                  <span className="text-eyebrow text-ink-400 font-mono whitespace-nowrap">
                    Shot on location — natural light
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 2. MARQUEE - values strip                               */}
        {/* ====================================================== */}
        <section className="bg-ink-900 text-cream-100 py-6 overflow-hidden">
          <div className="flex items-center gap-16 whitespace-nowrap animate-marquee">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-16 flex-shrink-0">
                {['Clear Vision', 'Hand‑Finished Frames', 'UV400 Protection', 'Lightweight Design', 'Blue Light Filter', 'Lifetime Craftsmanship', 'Free USA Shipping $75+'].map((t, j) => (
                  <span key={j} className="flex items-center gap-16">
                    <span className="font-display text-xl italic font-light text-cream-50">{t}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-copper-500" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* ====================================================== */}
        {/* 3. FEATURED PRODUCTS - magazine grid                    */}
        {/* ====================================================== */}
        <section className="py-20 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
              <div className="lg:col-span-7">
                <span className="text-eyebrow text-copper-600 font-mono">— 01 / Bestsellers</span>
                <h2 className="font-display text-ink-900 text-display-2xl mt-4">
                  Our most <span className="italic font-light">loved</span> frames.
                </h2>
              </div>
              <div className="lg:col-span-5 lg:text-right">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 text-ink-900 border-b border-ink-900 pb-1 hover:border-copper-600 hover:text-copper-600 transition-colors"
                >
                  <span className="text-sm tracking-[0.15em] uppercase font-mono">View all eyewear</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-16">
              {featuredProducts.map((product, idx) => (
                <Link key={product.id} to={`/product/${product.slug}`} className="group">
                  <div className="relative bg-cream-100 aspect-[4/5] overflow-hidden mb-5">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain p-6 group-hover:scale-[1.04] transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                    />
                    {product.badge && (
                      <span className="absolute top-4 left-4 px-3 py-1 bg-cream-50 text-ink-900 text-[10px] tracking-[0.2em] uppercase font-mono">
                        {product.badge}
                      </span>
                    )}
                    <span className="absolute top-4 right-4 text-[10px] tracking-[0.2em] uppercase font-mono text-ink-500">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    {/* Hover quick add */}
                    <button
                      onClick={(e) => handleAddToCart(e, product)}
                      className="absolute bottom-0 left-0 right-0 py-4 bg-ink-900 text-cream-50 text-sm tracking-[0.15em] uppercase font-mono translate-y-full group-hover:translate-y-0 transition-transform duration-400 hover:bg-copper-600"
                    >
                      Add to Bag
                    </button>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono mb-1">
                        {categories.find((c) => c.id === product.categoryId)?.name}
                      </p>
                      <h3 className="font-display text-lg text-ink-900 leading-tight">{product.name}</h3>
                    </div>
                    <p className="font-display text-lg text-ink-900 whitespace-nowrap">${product.price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 4. CATEGORY SHOWCASE - visual blocks                    */}
        {/* ====================================================== */}
        <section className="py-20 lg:py-28 bg-cream-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
              <div className="max-w-xl">
                <span className="text-eyebrow text-copper-600 font-mono">— 02 / Shop by style</span>
                <h2 className="font-display text-ink-900 text-display-2xl mt-4">
                  A style for <span className="italic font-light">every</span> moment.
                </h2>
              </div>
              <p className="text-ink-500 max-w-sm">
                Eight curated categories spanning everyday wear, sport, reading and smart eyewear.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-5">
              {categories.map((cat, i) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="group relative aspect-[3/4] bg-cream-50 overflow-hidden"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-contain p-6 group-hover:scale-[1.06] transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-ink-900/10 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-between p-5">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-cream-50/80 font-mono">
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-cream-50 text-xl lg:text-2xl leading-tight">{cat.name}</h3>
                      <div className="flex items-center gap-2 mt-2 text-cream-50/80">
                        <span className="text-xs tracking-[0.15em] uppercase font-mono">Shop</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 5. BENEFITS                                             */}
        {/* ====================================================== */}
        <section className="py-20 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="max-w-2xl mb-16">
              <span className="text-eyebrow text-copper-600 font-mono">— 03 / Designed for you</span>
              <h2 className="font-display text-ink-900 text-display-2xl mt-4">
                Four things we <span className="italic font-light">obsess</span> over.
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-200">
              {features.map((f, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={f.id} className="bg-cream-50 p-8 lg:p-10 hover:bg-cream-100 transition-colors">
                    <div className="flex items-center justify-between mb-8">
                      <Icon className="w-7 h-7 text-copper-600" strokeWidth={1.2} />
                      <span className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-mono">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl text-ink-900 mb-3 leading-tight">{f.title}</h3>
                    <p className="text-ink-500 text-sm leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 6. LIFESTYLE EDITORIAL - full bleed                     */}
        {/* ====================================================== */}
        <section className="relative min-h-[85vh] lg:min-h-[90vh] flex items-center">
          <div className="absolute inset-0">
            <img
              src={siteImages.lifestyleEditorial}
              alt="Clarity Lens Co editorial lifestyle"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/30 to-ink-900/10" />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 w-full py-24">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 lg:col-start-6">
                <span className="text-eyebrow text-copper-400 font-mono">— An editorial</span>
                <h2 className="font-display text-cream-50 text-display-2xl mt-4">
                  Everyday vision, <span className="italic font-light text-copper-400">elevated.</span>
                </h2>
                <p className="text-cream-200/80 text-lg mt-6 max-w-xl leading-relaxed">
                  Our frames are drawn from classic silhouettes then refined with modern materials, ultralight
                  hinges and a finish that feels as good as it looks. Because how you see the world matters.
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 mt-8 text-cream-50 border-b border-cream-50 pb-1 hover:text-copper-400 hover:border-copper-400 transition-colors"
                >
                  <span className="text-sm tracking-[0.15em] uppercase font-mono">Our story</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 7. WHY CHOOSE US                                        */}
        {/* ====================================================== */}
        <section className="py-20 lg:py-32 bg-cream-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid lg:grid-cols-12 gap-8 mb-16">
              <div className="lg:col-span-6">
                <span className="text-eyebrow text-copper-600 font-mono">— 04 / Why Clarity</span>
                <h2 className="font-display text-ink-900 text-display-2xl mt-4">
                  A promise in every <span className="italic font-light">pair.</span>
                </h2>
              </div>
              <p className="lg:col-span-5 lg:col-start-8 text-ink-500 text-lg leading-relaxed lg:pt-4">
                Craftsmanship, comfort and service that outlast seasons. Here&rsquo;s what you can count on when
                you shop with Clarity Lens Co.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyPoints.map((w, i) => (
                <div
                  key={i}
                  className="group relative bg-cream-50 p-8 border border-ink-100 hover:border-copper-400 transition-colors"
                >
                  <span className="font-display text-5xl italic font-light text-copper-400 mb-6 block">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-display text-xl text-ink-900 mb-3">{w.title}</h3>
                  <p className="text-ink-500 text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 8. CTA - See Better. Feel Better.                        */}
        {/* ====================================================== */}
        <section className="py-24 lg:py-32 bg-ink-900 text-cream-50 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img src={siteImages.ctaBackground} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 text-center">
            <span className="text-eyebrow text-copper-400 font-mono">— The Clarity promise</span>
            <h2 className="font-display text-cream-50 mt-6 leading-[0.95] text-[clamp(3rem,10vw,8rem)]">
              See better.
              <br />
              <span className="italic font-light text-copper-400">Feel better.</span>
            </h2>
            <p className="mt-8 text-cream-200/70 text-lg max-w-xl mx-auto">
              Find the frames that feel like an extension of you — with free USA shipping and 30-day returns.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/shop"
                className="group inline-flex items-center gap-3 px-10 py-4 bg-cream-50 text-ink-900 hover:bg-copper-500 hover:text-cream-50 transition-colors"
              >
                <span className="text-sm tracking-[0.15em] uppercase font-mono">Shop the collection</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-10 py-4 border border-cream-50/40 text-cream-50 hover:border-cream-50 transition-colors"
              >
                <span className="text-sm tracking-[0.15em] uppercase font-mono">Talk to us</span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
